import { useState } from "react";
import { homeService } from "@miplatform/api";
import { BUTTON_LABELS, HTTP_STATUSES, ICONS_WEB } from "@miplatform/constants";
import { useDebounce } from "@miplatform/hooks";
import {
  getSelectedAttributeValues,
  simpleSort,
  sortCategoryAttributes,
} from "@miplatform/utils";
import * as mdi from "@mdi/js";
import { useTheme } from "@mui/material";

export const useAdderDetailFormViewModel = ({ stateManager, setValue }) => {
  const [materials, setMaterials] = useState([]);
  const [filteredMaterials, setFilteredMaterials] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState({});
  const [selectedAttributeValues, setSelectedAttributeValues] = useState({});
  const [categoryFilterOptions, setCategoryFilterOptions] = useState([]);
  const [openMaterialResults, setOpenMaterialResults] = useState(false);
  const [openCategoryDrawer, setOpenCategoryDrawer] = useState(false);
  const [adderItems, setAdderItems] = useState([]);

  const theme = useTheme();

  const debouncedSearch = useDebounce(search, 300);
  const hasFilters = !!selectedCategory?.categoryId || !!debouncedSearch;

  const getCategoryFilters = () => {
    stateManager.run(async () => {
      const response = await homeService.categories.getFilters();
      if (response.status === HTTP_STATUSES.OK) {
        setCategoryFilterOptions(response.data);
      }
    });
  };

  const getMaterials = () => {
    stateManager.run(async () => {
      const response = await homeService.materials.getAll({
        sort: { category: ["categoryName", "itemCode"] },
        filters: {
          dependsOn: { any: [] },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        setMaterials(response.data);
      }
    });
  };

  const handleSearch = (value) => {
    setSearch(value);
    setOpenMaterialResults(true);
  };

  const handleAttributeValueClick = (attributeId, attributeValue) => {
    getSelectedAttributeValues(
      attributeId,
      attributeValue,
      selectedAttributeValues,
      setSelectedAttributeValues
    );
  };

  const handleCategoryClick = (category) => {
    const sortedCategory = sortCategoryAttributes(category);
    setSelectedCategory(sortedCategory);
    setOpenMaterialResults(true);
  };

  const handleResetSelection = () => {
    setSelectedAttributeValues({});
    setSearch("");
  };

  const handleReset = () => {
    setSelectedCategory({});
    handleResetSelection();
  };

  const handleResetResultContainer = () => {
    setSearch("");
    setSelectedAttributeValues({});
    setSelectedCategory({});
    setFilteredMaterials([]);
    setOpenMaterialResults(false);
  };

  const handleCloseCategoryDrawer = () => {
    setOpenCategoryDrawer(false);
    handleResetResultContainer();
  };

  const isDataSelected = (item) =>
    adderItems.some((d) => d.id === item.id && !d.removed);

  const removeData = (data, id) => {
    const isNewItem = data.some((d) => d.id === id && d.isNew);
    const softRemovedData = data.map((d) =>
      d.id === id ? { ...d, removed: true } : d
    );
    const removedData = data.filter((d) => d.id !== id);
    return isNewItem ? removedData : softRemovedData;
  };

  const addData = (data, item) => {
    const itemExists = data.some((d) => d.id === item.id);
    const undoRemovedData = data.map((d) =>
      d.id === item.id ? { ...item, removed: false } : d
    );
    const newData = [...data, { ...item, isNew: true }];
    return itemExists ? undoRemovedData : newData;
  };

  const getIdsFromData = (updatedData) =>
    updatedData.filter((d) => !d.removed).map((d) => d.id);

  const handleAddRemoveData = (item) => {
    const isSelected = isDataSelected(item);
    const updatedData = isSelected
      ? removeData(adderItems, item.id)
      : addData(adderItems, item);
    const sortedData = updatedData.sort((a, b) =>
      simpleSort(a.category?.itemCode, b.category?.itemCode)
    );
    setAdderItems(sortedData);
    const ids = getIdsFromData(sortedData);
    setValue("materialIds", ids, { shouldValidate: true, shouldDirty: true });
  };

  const getMenuOptions = (item) => {
    const options = [];
    if (!item.removed) {
      options.push({
        label: "Remove Item",
        icon: mdi[ICONS_WEB.TRASH_CAN],
        color: theme.palette.red.main,
        action: () => handleAddRemoveData(item),
      });
    }
    if (item.removed) {
      options.push({
        label: BUTTON_LABELS.UNDO,
        icon: mdi[ICONS_WEB.ARROW_U_LEFT_TOP],
        action: () => handleAddRemoveData(item),
      });
    }
    return options;
  };

  return {
    materials,
    categoryFilterOptions,
    selectedCategory,
    selectedAttributeValues,
    debouncedSearch,
    search,
    filteredMaterials,
    openMaterialResults,
    openCategoryDrawer,
    hasFilters,
    adderItems,
    getMaterials,
    getCategoryFilters,
    getMenuOptions,
    handleSearch,
    handleCategoryClick,
    handleAttributeValueClick,
    handleReset,
    handleResetResultContainer,
    handleCloseCategoryDrawer,
    handleAddRemoveData,
    isDataSelected,
    setFilteredMaterials,
    setOpenCategoryDrawer,
    setAdderItems,
  };
};
